import React from 'react';
import { Box, Modal, Stack, Typography, useTheme, Chip, Divider } from '@mui/material';
import { Order } from 'types/Order';
import { OrderDetailsView } from '../OrderDetailsView/OrderDetailsView';
import PersonIcon from '@mui/icons-material/Person';
import { formatCustomDate, toCurrencyFormat } from 'utils/formatter';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

type OrderHistoryModalProps = {
  order: Order;
  handleClose: () => void;
  isModalOpen: boolean;
};

const OrderHistoryModal: React.FC<OrderHistoryModalProps> = ({
  order,
  handleClose,
  isModalOpen
}) => {
  const { palette } = useTheme();

  const getStatusChip = () => {
    if (order.isCanceled) {
      return <Chip label="Cancelado" sx={{ backgroundColor: '#C0392B', color: '#fff' }} />;
    }
    if (order.isRejected) {
      return <Chip label="Rechazado" sx={{ backgroundColor: '#E74C3C', color: '#fff' }} />;
    }
    if (order.currentOrderProcessStep?.status === 'delivered') {
      return <Chip label="Completado" sx={{ backgroundColor: '#0e766e', color: '#fff' }} />;
    }
    return <Chip label="En proceso" sx={{ backgroundColor: '#3498DB', color: '#fff' }} />;
  };

  const formatOrderDate = (dateString: string) => {
    return format(parseISO(dateString), "EEEE, d MMMM yyyy 'a las' HH:mm", { locale: es });
  };

  return (
    <Modal open={isModalOpen} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 650,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: '16px',
          overflow: 'hidden',
          outline: 'none',
          '&:focus': {
            outline: 'none'
          }
        }}>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #EF3D32 0%, #ffaf45 200%)',
            color: '#fff',
            p: 3,
            position: 'relative',
            backdropFilter: 'blur(10px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(5px)',
              zIndex: 1
            },
            '& > *': {
              position: 'relative',
              zIndex: 2
            }
          }}>
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              px: 2,
              py: 1,
              borderRadius: 2
            }}>
            <Typography fontWeight={600}>#{order.id}</Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
              Detalle de Orden
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, textTransform: 'capitalize' }}>
              {formatOrderDate(order.createdAt)}
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Status and Total */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Estado de la orden
                </Typography>
                {getStatusChip()}
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Total
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {toCurrencyFormat(parseFloat(order.productsTotal))}
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Order Details */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Productos
              </Typography>
              <OrderDetailsView order={order} hideTotal={true} />
            </Box>

            {/* Additional Order Information */}
            {order.notes && (
              <>
                <Divider />
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Notas
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {order.notes}
                  </Typography>
                </Box>
              </>
            )}
            <Divider />
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default OrderHistoryModal;
